import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Checkbox, Grid, Header, Icon, Input, Popup } from "semantic-ui-react";
import { openModal } from "../../../../app/common/modals/modalSlice";
import {
  searchFilter,
  downloadAllDocumentsAsPdf,
} from "../../../../app/common/util/util";
import { updateTransactionSettingsInDb } from "../../../../app/firestore/firestoreService";
import DocTransActiveList from "./DocTransActiveList";
import DocPendingNotifications from "../../docComponents/DocPendingNotifications";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";

export default function DocTransActiveDashboard() {
  const dispatch = useDispatch();
  const { docsTransActive, docsTransArchived, docsTransShared } = useSelector(
    (state) => state.doc
  );
  const { transaction } = useSelector((state) => state.transaction);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const [searchTerms, setSearchTerms] = useState("");
  const [downloadingAll, setDownloadingAll] = useState(false);
  const docs = searchFilter(docsTransActive?.docs, searchTerms);
  const docsArchived = searchFilter(docsTransArchived?.docs, searchTerms);
  const docsShared = searchFilter(docsTransShared?.docs, searchTerms);
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });

  // Get all documents for download (active + archived + shared)
  const allDocs = [
    ...(docsTransActive?.docs || []),
    ...(docsTransArchived?.docs || []),
    ...(docsTransShared?.docs || []),
  ];

  // Check if there are non-PDF documents to determine button text
  const hasNonPdfDocs = allDocs.some(
    (doc) => doc.filetype && doc.filetype !== "pdf"
  );
  const downloadButtonText = hasNonPdfDocs
    ? "Download All as ZIP"
    : "Download All as PDF";

  // Group archived documents into folders vs. plain archived sections by label,
  // so downloads can prompt the user about which of these to include
  const archivedDocsForDownload = docsTransArchived?.docs || [];
  const folderDocsForDownload = archivedDocsForDownload.filter(
    (doc) => doc.isFolder === true
  );
  const plainArchivedDocsForDownload = archivedDocsForDownload.filter(
    (doc) => doc.isFolder !== true
  );
  const folderGroupsForDownload = Object.entries(
    folderDocsForDownload.reduce((groups, doc) => {
      const label = doc.archivedLabel || "Folder";
      if (!groups[label]) groups[label] = [];
      groups[label].push(doc);
      return groups;
    }, {})
  )
    .map(([label, docs]) => ({ label, docs }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const archivedGroupsForDownload = Object.entries(
    plainArchivedDocsForDownload.reduce((groups, doc) => {
      const label = doc.archivedLabel || "Archived";
      if (!groups[label]) groups[label] = [];
      groups[label].push(doc);
      return groups;
    }, {})
  )
    .map(([label, docs]) => ({ label, docs }))
    .sort((a, b) => {
      if (a.label === "Archived" && b.label !== "Archived") return 1;
      if (b.label === "Archived" && a.label !== "Archived") return -1;
      return a.label.localeCompare(b.label);
    });

  const forceQueueNotifications =
    !!currentUserProfile?.notificationPreferences?.forceQueueNotifications;

  async function handleToggleQueueNotifications(checked) {
    try {
      await updateTransactionSettingsInDb(transaction.id, {
        queueNotifications: checked,
      });
    } catch (error) {
      toast.error(error.message || "Failed to update notification setting");
    }
  }

  // If the profile is set to only allow queuing notifications, keep
  // transactions created before that setting was turned on in sync.
  useEffect(() => {
    if (
      forceQueueNotifications &&
      transaction?.id &&
      transaction?.queueNotifications !== true
    ) {
      handleToggleQueueNotifications(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceQueueNotifications, transaction?.id, transaction?.queueNotifications]);

  async function handleDownloadAll() {
    if (allDocs.length === 0) {
      toast.error("No documents available to download");
      return;
    }

    if (
      folderGroupsForDownload.length > 0 ||
      archivedGroupsForDownload.length > 0
    ) {
      dispatch(
        openModal({
          modalType: "DocDownloadAllOptionsModal",
          modalProps: {
            baseDocs: [
              ...(docsTransActive?.docs || []),
              ...(docsTransShared?.docs || []),
            ],
            folderGroups: folderGroupsForDownload,
            archivedGroups: archivedGroupsForDownload,
            transaction,
            currentUserProfile,
          },
        })
      );
      return;
    }

    setDownloadingAll(true);
    try {
      await downloadAllDocumentsAsPdf(allDocs, transaction, currentUserProfile);
      const successMessage = hasNonPdfDocs
        ? "All documents downloaded as ZIP file successfully!"
        : "All documents downloaded as merged PDF successfully!";
      toast.success(successMessage);
    } catch (error) {
      console.error("Error downloading all documents:", error);
      toast.error(error.message || "Failed to download documents");
    } finally {
      setDownloadingAll(false);
    }
  }

  return (
    <div className="secondary-page-wrapper">
      <Grid stackable>
        <Grid.Column computer={8}>
          <Input
            type="text"
            fluid
            placeholder="Search by document name or status"
            value={searchTerms}
            onChange={(e) => setSearchTerms(e.target.value)}
          ></Input>
        </Grid.Column>
        <Grid.Column width={8}>
          <div className={isMobile ? null : "float-right"}>
            <Popup
              content={
                hasNonPdfDocs
                  ? "Downloads all PDFs merged into one file plus individual non-PDF files in a ZIP archive"
                  : "Downloads all PDF documents merged into a single PDF file"
              }
              trigger={
                <Button
                  icon="download"
                  size="small"
                  className={isMobile ? "fluid bottom margin" : "right margin"}
                  onClick={handleDownloadAll}
                  content={downloadButtonText}
                  color="teal"
                  loading={downloadingAll}
                  disabled={downloadingAll || allDocs.length === 0}
                />
              }
            />
            <Button
              to="#"
              icon="plus"
              size="small"
              className={isMobile ? "fluid" : null}
              onClick={() =>
                dispatch(
                  openModal({
                    modalType: "DocAddDocumentsModal",
                  })
                )
              }
              content="Add Documents"
              color="blue"
            />
          </div>
        </Grid.Column>

        <Grid.Column computer={16} className="zero top padding">
          <Popup
            content={
              forceQueueNotifications
                ? "Your profile is set to only allow queuing notifications. To turn this off, go to your profile and uncheck \"Only allow queuing notifications.\""
                : "When on, sharing and signature requests are queued in Pending Notifications instead of prompting for an email each time. You can then send one customizable email per group of recipients."
            }
            trigger={
              <Checkbox
                toggle
                label="Queue notifications (send grouped emails later)"
                checked={forceQueueNotifications || !!transaction?.queueNotifications}
                disabled={forceQueueNotifications}
                onChange={(e, { checked }) =>
                  handleToggleQueueNotifications(checked)
                }
              />
            }
          />
        </Grid.Column>

        <DocPendingNotifications />

        <Grid.Column computer={16}>
          {docsArchived?.length > 0 && (
            <Header className="zero bottom margin">Active</Header>
          )}
          {docs?.length > 0 ? (
            <DocTransActiveList
              docs={docs}
              column={docsTransActive.column || "updatedAt"}
              direction={docsTransActive.direction || "descending"}
            />
          ) : (
            <p>There are no active documents</p>
          )}
        </Grid.Column>
        {docsShared?.length > 0 && (
          <Grid.Column computer={16}>
            <Header className="zero bottom margin">Shared</Header>
            <DocTransActiveList
              docs={docsShared}
              column={docsTransShared.column || "updatedAt"}
              direction={docsTransShared.direction || "descending"}
              shared={true}
            />
          </Grid.Column>
        )}
        {docsArchived?.length > 0 &&
          (() => {
            const folderDocs = docsArchived.filter((doc) => doc.isFolder === true);
            const plainArchivedDocs = docsArchived.filter((doc) => doc.isFolder !== true);

            // Group folder documents by archivedLabel
            const groupedFolders = folderDocs.reduce((groups, doc) => {
              const label = doc.archivedLabel || "Folder";
              if (!groups[label]) {
                groups[label] = [];
              }
              groups[label].push(doc);
              return groups;
            }, {});
            const sortedFolderLabels = Object.keys(groupedFolders).sort((a, b) =>
              a.localeCompare(b)
            );

            // Group archived documents by archivedLabel
            const groupedArchived = plainArchivedDocs.reduce((groups, doc) => {
              const label = doc.archivedLabel || "Archived";
              if (!groups[label]) {
                groups[label] = [];
              }
              groups[label].push(doc);
              return groups;
            }, {});

            // Sort labels alphabetically, but put 'Archived' (unlabeled) last
            const sortedLabels = Object.keys(groupedArchived).sort((a, b) => {
              if (a === "Archived" && b !== "Archived") return 1;
              if (b === "Archived" && a !== "Archived") return -1;
              return a.localeCompare(b);
            });

            return (
              <>
                {sortedFolderLabels.map((label) => (
                  <Grid.Column computer={16} key={`folder-${label}`}>
                    <Header className="zero bottom margin">
                      <Icon name="folder open outline" />
                      {label}
                    </Header>
                    <DocTransActiveList
                      docs={groupedFolders[label]}
                      column={docsTransArchived.column || "updatedAt"}
                      direction={docsTransArchived.direction || "descending"}
                      archived={true}
                    />
                  </Grid.Column>
                ))}
                {sortedLabels.map((label) => (
                  <Grid.Column computer={16} key={`archived-${label}`}>
                    <Header className="zero bottom margin">
                       <Icon name="archive" />

                      {label === "Archived" ? "Archived" : `Archived - ${label}`}
                    </Header>
                    <DocTransActiveList
                      docs={groupedArchived[label]}
                      column={docsTransArchived.column || "updatedAt"}
                      direction={docsTransArchived.direction || "descending"}
                      archived={true}
                    />
                  </Grid.Column>
                ))}
              </>
            );
          })()}
      </Grid>
    </div>
  );
}
